import {
  addTextBlockInProjectElement,
  createProjectElement,
  createProjectElementWithText,
  importWorkOrDetailAsPeBulk,
  deleteProjectElement,
  deleteTextBlockInPE,
  getProjectElementList,
  reorderProjectElement,
  updateProjectElement,
  updateProjectElementBulkInfo,
  updatePEBulk,
} from '../../api/projectElementApi';
import { useProjectElementListStore } from '../../store/projectElementStore';
import { ProjectElementData } from '../../dto/EntityRepository';
import {
  CreateProjectElementReq,
  CreateProjectElementTextBlockWithTextBlockReq,
  CreateProjectElementWithTextReq,
  DeleteProjectElementReq,
  DeleteTextBlockInPEReq,
  ImportWorkOrDetailAsPeBulkReq,
  UpdatePEBulkReq,
  UpdateProjectElementBulkInfoReq,
  UpdateProjectElementReq,
  UpdateReorderListReq,
} from '../../dto/ReqDtoRepository';
import {
  ProjectElementListResponse,
  ProjectElementResponse,
} from '../../dto/ResDtoRepository';
import { useTempAlertStore } from '../../store/portal/tempAlertStore';
import { useApiWrapper } from './apiWrapper';
import { useImage } from './useImage';
import { useFile } from './useFile';
import { usePageOwnerIdentityStore } from '../../store/pageOwnerIdentityStore';
import { useModalStore } from '../../store/portal/modalStore';
import { runCurrentArchiveRequest } from '../../util/currentArchiveRequest';
import { toUpdateProjectElementBulkInfoReq } from '../../util/projectElementBulkInfo';
import type { ResourceScope, ResourceStatus } from '../../util/scopedResource';

const scrollToCreatedProjectElement = (peId?: string) => {
  if (!peId) return;
  // 미저장 항목 스크롤과 동일하게 DOM 반영을 기다린다.
  setTimeout(() => {
    document.getElementById(`project-element-${peId}`)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, 100);
};


interface UseProjectElementStateResult {
  projectElementList: ProjectElementData[];
  listOwnerAui: string | null;
  listScope: ResourceScope | null;
  listProjectId: string | null;
  status: ResourceStatus;
}

interface UseProjectElementActionsResult {
  getProjectElementList: (projectId: string) => Promise<void>;
  getProjectElementListOwner: (projectId: string) => Promise<void>;
  createProjectElement: (data: CreateProjectElementReq) => Promise<void>;
  updateProjectElement: (data: UpdateProjectElementReq) => Promise<void>;
  deleteProjectElement: (data: DeleteProjectElementReq) => Promise<void>;
  deleteTextBlockInPE: (data: DeleteTextBlockInPEReq) => Promise<void>;
  importWorkOrDetailAsPeBulk: (data: ImportWorkOrDetailAsPeBulkReq) => Promise<void>;
  createProjectElementWithText: (data: CreateProjectElementWithTextReq) => Promise<void>;
  addTextBlockInProjectElement: (data: CreateProjectElementTextBlockWithTextBlockReq) => Promise<void>;
  reorderProjectElement: (data: UpdateReorderListReq) => Promise<void>;
  updateProjectElementBulk: (projectId: string, data: UpdatePEBulkReq) => Promise<void>;
  updateProjectElementBulkInfo: (data: UpdateProjectElementBulkInfoReq) => Promise<void>;
  /** hasChanged인 항목만 모아 bulk-info 수정. 없으면 요청하지 않는다. */
  updateChangedProjectElementBulkInfo: () => Promise<void>;
}

export const useProjectElementState = (): UseProjectElementStateResult => {
  const projectElementList = useProjectElementListStore((state) => state.projectElementList);
  const listOwnerAui = useProjectElementListStore((state) => state.listOwnerAui);
  const listScope = useProjectElementListStore((state) => state.listScope);
  const listProjectId = useProjectElementListStore((state) => state.listProjectId);
  const status = useProjectElementListStore((state) => state.status);
  return { projectElementList, listOwnerAui, listScope, listProjectId, status };
};

export const useProjectElementActions = (): UseProjectElementActionsResult => {
  const pageOwnerAui = usePageOwnerIdentityStore((state) => state.pageOwner?.aui);
  const setProjectElementList = useProjectElementListStore((state) => state.setProjectElementList);
  const beginProjectElementListFetch = useProjectElementListStore((state) => state.beginProjectElementListFetch);
  const setProjectElementListWithScope = useProjectElementListStore((state) => state.setProjectElementListWithScope);
  const setProjectElementListError = useProjectElementListStore((state) => state.setProjectElementListError);
  const addProjectElementList = useProjectElementListStore((state) => state.addProjectElementList);
  const afterCreateProjectElement = useProjectElementListStore((state) => state.afterCreateProjectElement);
  const afterUpdateProjectElement = useProjectElementListStore((state) => state.afterUpdateProjectElement);
  const afterUpdateProjectElementBulkInfo = useProjectElementListStore((state) => state.afterUpdateProjectElementBulkInfo);
  const afterDeleteProjectElement = useProjectElementListStore((state) => state.afterDeleteProjectElement);
  const afterAddTextBlockInPE = useProjectElementListStore((state) => state.afterAddTextBlockInPE);
  const afterDeleteTextBlockInPE = useProjectElementListStore((state) => state.afterDeleteTextBlockInPE);
  const setUpdatedTempAlert = useTempAlertStore((state) => state.setUpdatedTempAlert);
  const setDeletedTempAlert = useTempAlertStore((state) => state.setDeletedTempAlert);
  const clearModal = useModalStore((state) => state.clearModal);
  const withApiHandler = useApiWrapper();
  const { uploadPEImage } = useImage();
  const { uploadPEPdf } = useFile();

  const handleProjectElementSuccess = (
    response: ProjectElementListResponse,
    ownerAui: string,
    scope: ResourceScope,
    projectId: string,
  ) => {
    const projectElementList = response.data;
    setProjectElementListWithScope(projectElementList, ownerAui, scope, projectId);
  };

  const handleImportWorkOrDetailAsPeBulkSuccess = (response: ProjectElementListResponse) => {
    const importedProjectElementList = response.data;
    addProjectElementList(importedProjectElementList);
    clearModal();
    scrollToCreatedProjectElement(importedProjectElementList[0]?.id);
  };

  const handleCreateProjectElementSuccess = (response: ProjectElementResponse) => {
    const createdPe = response.data;
    afterCreateProjectElement(createdPe);
    clearModal(400);
    scrollToCreatedProjectElement(createdPe.id);
  };

  const handleAddTextBlockInPESuccess = (response: ProjectElementResponse) => {
    const createdPe = response.data;
    afterAddTextBlockInPE(createdPe);
    clearModal();
  };

  const handleUpdateProjectElementSuccess = (response: ProjectElementResponse) => {
    const updated = response.data;
    afterUpdateProjectElement(updated);
    setUpdatedTempAlert();
  };

  const handleDeleteProjectElementSuccess = (projectElementId: string) => {
    afterDeleteProjectElement(projectElementId);
    console.log("deleted well");
    setDeletedTempAlert();
  };

  const handleDeleteTextBlockInPESuccess = (
    projectElementId: string,
    textBlockId: string,
  ) => {
    afterDeleteTextBlockInPE(projectElementId, textBlockId);
    console.log("deleted well");
    setDeletedTempAlert();
  };

  const handleReorderProjectElementSuccess = (response: ProjectElementListResponse) => {
    const projectElementList = response.data;
    setProjectElementList(projectElementList);
  };

  const handleUpdatePEBulkSuccess = (response: ProjectElementListResponse) => {
    const projectElementList = response.data;

    setUpdatedTempAlert();
    addProjectElementList(projectElementList);
    clearModal();
    scrollToCreatedProjectElement(projectElementList[0]?.id);
  };

  const handleUpdateProjectElementBulkInfoSuccess = (response: ProjectElementListResponse) => {
    afterUpdateProjectElementBulkInfo(response.data);
    setUpdatedTempAlert();
  };

  const fetchProjectElementList = async (aui: string, scope: ResourceScope, projectId: string) => {
    beginProjectElementListFetch(aui, scope, projectId);
    try {
      return await runCurrentArchiveRequest(
        aui,
        () => getProjectElementList(aui, projectId),
        (response) => handleProjectElementSuccess(response, aui, scope, projectId),
        { matchOwnerAccess: false },
      );
    } catch (error) {
      if (useProjectElementListStore.getState().status === 'loading') {
        setProjectElementListError();
      }
      throw error;
    }
  };

  const handleProjectElementRequest = async (
    action: 'get' | 'get_for_owner' | 'create' | 'update' | 'update bulk' | 'update bulk info' | 'delete' | 'import work or detail bulk' | 'import text' | 'add text block' | 'reorder' | 'delete text block',
    projectId: string | null,
    data?:
    CreateProjectElementReq |
    UpdateProjectElementReq |
    UpdatePEBulkReq |
    UpdateProjectElementBulkInfoReq |
    DeleteProjectElementReq |
    ImportWorkOrDetailAsPeBulkReq |
    CreateProjectElementWithTextReq |
    CreateProjectElementTextBlockWithTextBlockReq |
    UpdateReorderListReq
  ) => {
    if (!pageOwnerAui) return;
    const aui = pageOwnerAui;
    const apiFunction = async () => {
      switch (action) {
        case 'create':
          return runCurrentArchiveRequest(
            aui,
            () => createProjectElement(aui, data as CreateProjectElementReq),
            handleCreateProjectElementSuccess,
          );
        case 'update':
          return runCurrentArchiveRequest(
            aui,
            () => updateProjectElement(aui, data as UpdateProjectElementReq),
            handleUpdateProjectElementSuccess,
          );
        case 'update bulk':
          return runCurrentArchiveRequest(
            aui,
            () => updatePEBulk(aui, data as UpdatePEBulkReq),
            handleUpdatePEBulkSuccess,
          );
        case 'update bulk info':
          return runCurrentArchiveRequest(
            aui,
            () => updateProjectElementBulkInfo(aui, data as UpdateProjectElementBulkInfoReq),
            handleUpdateProjectElementBulkInfoSuccess,
          );
        case 'delete': {
          const projectElementId = (data as DeleteProjectElementReq).projectElementId;
          return runCurrentArchiveRequest(
            aui,
            () => deleteProjectElement(aui, data as DeleteProjectElementReq),
            () => handleDeleteProjectElementSuccess(projectElementId),
          );
        }
        case 'delete text block': {
          const {
            projectElementId,
            textBlockId,
          } = data as DeleteTextBlockInPEReq;
          return runCurrentArchiveRequest(
            aui,
            () => deleteTextBlockInPE(aui, data as DeleteTextBlockInPEReq),
            () => handleDeleteTextBlockInPESuccess(
              projectElementId,
              textBlockId,
            ),
          );
        }
        case 'import work or detail bulk':
          return runCurrentArchiveRequest(
            aui,
            () => importWorkOrDetailAsPeBulk(
              aui,
              data as ImportWorkOrDetailAsPeBulkReq,
            ),
            handleImportWorkOrDetailAsPeBulkSuccess,
          );
        case 'import text':
          return runCurrentArchiveRequest(
            aui,
            () => createProjectElementWithText(
              aui,
              data as CreateProjectElementWithTextReq,
            ),
            handleCreateProjectElementSuccess,
          );
        case 'add text block':
          return runCurrentArchiveRequest(
            aui,
            () => addTextBlockInProjectElement(
              aui,
              data as CreateProjectElementTextBlockWithTextBlockReq,
            ),
            handleAddTextBlockInPESuccess,
          );
        case 'reorder':
          return runCurrentArchiveRequest(
            aui,
            () => reorderProjectElement(aui, data as UpdateReorderListReq),
            handleReorderProjectElementSuccess,
          );
        case 'get_for_owner':
          return fetchProjectElementList(aui, 'owner', projectId as string);
        case 'get':
        default:
          return fetchProjectElementList(aui, 'public', projectId as string);
      }
    };

    await withApiHandler(apiFunction, [aui, action, projectId, data]);
  };

  const getProjectElementHandler = (projectId: string) => handleProjectElementRequest('get', projectId);
  const getProjectElementListOwnerHandler = (projectId: string) => handleProjectElementRequest('get_for_owner', projectId);
  const createProjectElementHandler = (data: CreateProjectElementReq) => handleProjectElementRequest('create', null, data);
  const updateProjectElementHandler = (data: UpdateProjectElementReq) => handleProjectElementRequest('update', null, data);
  const deleteProjectElementHandler = (data: DeleteProjectElementReq) => handleProjectElementRequest('delete', null, data);
  const deleteTextBlockInPEHandler = (data: DeleteTextBlockInPEReq) => handleProjectElementRequest('delete text block', null, data);
  const importWorkOrDetailAsPeBulkHandler = (data: ImportWorkOrDetailAsPeBulkReq) => handleProjectElementRequest('import work or detail bulk', null, data);
  const createProjectElementWithTextHandler = (data: CreateProjectElementWithTextReq) => handleProjectElementRequest('import text', null, data);
  const addTextBlockInProjectElementHandler = (data: CreateProjectElementTextBlockWithTextBlockReq) => handleProjectElementRequest('add text block', null, data);
  const reorderProjectElementHandler = (data: UpdateReorderListReq) => handleProjectElementRequest('reorder', null, data);
  const updatePEBulkHandler = (projectId: string, data: UpdatePEBulkReq) => handleProjectElementRequest('update bulk', projectId, data);
  const updateProjectElementBulkInfoHandler = (data: UpdateProjectElementBulkInfoReq) => handleProjectElementRequest('update bulk info', data.projectId, data);
  const updateChangedProjectElementBulkInfoHandler = async () => {
    const { listProjectId, projectElementList } = useProjectElementListStore.getState();
    if (!listProjectId) return;
    const request = await toUpdateProjectElementBulkInfoReq(
      listProjectId,
      projectElementList,
      uploadPEImage,
      uploadPEPdf,
    );
    if (!request) return;
    return handleProjectElementRequest('update bulk info', request.projectId, request);
  };

  return {
    getProjectElementList: getProjectElementHandler,
    getProjectElementListOwner: getProjectElementListOwnerHandler,
    createProjectElement: createProjectElementHandler,
    updateProjectElement: updateProjectElementHandler,
    updateProjectElementBulk: updatePEBulkHandler,
    updateProjectElementBulkInfo: updateProjectElementBulkInfoHandler,
    updateChangedProjectElementBulkInfo: updateChangedProjectElementBulkInfoHandler,
    deleteProjectElement: deleteProjectElementHandler,
    deleteTextBlockInPE: deleteTextBlockInPEHandler,
    importWorkOrDetailAsPeBulk: importWorkOrDetailAsPeBulkHandler,
    createProjectElementWithText: createProjectElementWithTextHandler,
    addTextBlockInProjectElement: addTextBlockInProjectElementHandler,
    reorderProjectElement: reorderProjectElementHandler,
  };
};
